import { DialogDefinition } from '../../../services/overlay.service';
import { Observable } from 'rxjs';
import { SapiEntity, SapiEntityVersion } from '../../../services/sapi.service';
import { LineupService } from '../../../services/lineup.service';
import { Subscription } from 'rxjs';
import { Lineup } from '../../../interfaces/lineup';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { animate, sequence, style, transition, trigger } from '@angular/animations';
import { LineupDetailsConfigurationComponent } from './lineup-details-configuration.component';
import { Mediaclip } from 'app/interfaces/mediaclip';
import { FilterSet } from 'app/services/filter-set.service';
import { Playlist } from 'app/interfaces/playlist';

interface ErrorMessage {
	error: string;
	createdDate: string;
}
@Component({
	selector: 'lineup-details',
	templateUrl: './lineup-details.component.html',
	styleUrls: ['./lineup-details.component.scss'],
	animations: [
		trigger('anim', [
			transition('* => void', [
				style({ height: '*', opacity: '1', transform: 'translateX(0)', 'box-shadow': '0 1px 4px 0 rgba(0, 0, 0, 0.3)' }),
				sequence([
					animate('.25s ease', style({ height: '*', opacity: '.2', transform: 'translateX(20px)', 'box-shadow': 'none' })),
					animate('.1s ease', style({ height: '0', opacity: 0, transform: 'translateX(20px)', 'box-shadow': 'none' }))
				])
			]),
			transition('void => *', [
				style({ height: '0', opacity: '0', transform: 'translateX(20px)', 'box-shadow': 'none' }),
				sequence([
					animate('.1s ease', style({ height: '*', opacity: '.2', transform: 'translateX(20px)', 'box-shadow': 'none' })),
					animate('.35s ease', style({ height: '*', opacity: 1, transform: 'translateX(0)', 'box-shadow': '0 1px 4px 0 rgba(0, 0, 0, 0.3)' }))
				])
			])
		])
	],
})

export class LineupDetailsComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {

	@ViewChild('formRef', { static: true }) form: NgForm;
	@ViewChild(LineupDetailsConfigurationComponent) lineupDetailsConfigurationComponent: LineupDetailsConfigurationComponent;
	embedcode = '';
	baseurl: string;
	entity: Lineup;

	tabInfoIcons: any = {};
	showVersions = false;
	liveCountExceeded = false;
	subscriptions = new Subscription();
	channelUpdates: any[] = [];
	errorMessagesLog: ErrorMessage[] = [];
	updatingStatus = false;
	usageQuantity: any = {};
	hoursOnline: any = 0;

	mediaclip: Mediaclip;

	playlistsInLineupSchedule: string[];
	containsSlideshowPlaylist = false;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		public lineupService: LineupService
	) {

		super(route, location);
		this.entityConfiguration = {
			name: 'lineup',
			entityType: 'Lineup',
			parentPath: '/library/lineups',
			basePath: '/library/lineup/',
			versioned: true,
			showVersionMetadata: true,
			defaultVersionState: 'working',
			allowInvalidDrafts: true,
			versionMetadataToSave: {
				bbcomment: ''
			},
			defaults: {
				status: 'draft',
				hlsMethod: 'pull',
				inputLossColor: '#000000',
				schedule: {
					blocks: [{
						cycles: []
					},
					{
						cycles: []
					},
					{
						cycles: []
					},
					{
						cycles: []
					},
					{
						cycles: []
					},
					{
						cycles: []
					},
					{
						cycles: []
					}]
				}
			}
		};
	}

	ngOnInit() {
		super.ngOnInit();

		this.checklistItems = [
			{ key: 'hls', label: 'HLS', checked: false },
			{ key: 'schedule', label: 'SCHEDULE', checked: false },
			{ key: 'fallbacks', label: 'FALLBACKS', checked: false },
			{ key: 'audiostream', label: 'AUDIO_STREAM', checked: false, hidden: true }
		];

		// for checklist updates
		this.subscriptions.add(this.form.form.valueChanges.subscribe(() => {
			setTimeout(() => {
				this.updateChecklistStatus();
				this.setContainsSlideshowPlaylist();
			});
		}));

		this.subscriptions.add(this.lineupService.liveItemsSubject.subscribe(liveItems => {
			this.liveCountExceeded = liveItems > this.lineupService.maxLiveCount;
			this.updateChecklistStatus();
		}));

		this.updateChecklistStatus();
		this.getErrorLogHistory();

		this.subscriptions.add(this.lineupService.channelUpdateSubject.subscribe((msg) => {
			if (!this.entity || this.entity.id !== msg.id) {
				return;
			}
			if (msg.ev === 'notification' && msg.payload && msg.payload.message) {
				this.channelUpdates.unshift({
					time: new Date(),
					message: msg.payload.message,
					type: msg.payload.type
				});
			} else if (msg.ev === 'entityUpdate' && msg.payload && msg.payload.channelRestarting !== undefined) {
				this.entity.channelRestarting = msg.payload.channelRestarting;
				if (this.entity.channelRestarting) {
					this.channelUpdates.unshift({
						time: new Date(),
						message: `Channel restarting`,
						type: 'notification'
					});
				}
			} else if (msg.ev === 'entityUpdate' && msg.payload && msg.payload.channelState && this.entity.channelState !== msg.payload.channelState) {
				const s = msg.payload.channelState;
				this.entity.channelState = msg.payload.channelState;
				this.channelUpdates.unshift({
					time: new Date(),
					message: `Channel state updated to: ${this.translate.instant(msg.payload.channelState)}`,
					type: 'statechange'
				});

				if (s !== 'IDLE' && s !== 'RUNNING') {
					this.updatingStatus = true;
				} else {
					this.updatingStatus = false;
				}

				// payload.channelState =
				// 'CREATING'
				// 'CREATE_FAILED'
				// 'IDLE'
				// 'STARTING'
				// 'RUNNING'
				// 'RECOVERING'
				// 'STOPPING'
				// 'DELETING'
				// 'DELETED'
				// 'UPDATING'
				// 'UPDATE_FAILED'
			}
		}));
	}

	onStartClick() {
		this.updatingStatus = true;
		this.lineupService.start(this.entity.id);
	}

	onRestartClick() {
		this.updatingStatus = true;
		this.lineupService.restart(this.entity.id);
	}

	onStopClick() {
		this.updatingStatus = true;
		this.lineupService.stop(this.entity.id);
	}

	getErrorLogHistory(): void {
		if (this.activeId !== 'new') {
			this.sapiService.get('lineup', this.activeId, 'errorMessages').subscribe(result => {
				const messages = result as ErrorMessage[];
				this.errorMessagesLog = messages;
			});
		};
	};

	updateChecklistStatus() {
		if (!this.entity) {
			return;
		}
		const lineup = this.entity as Lineup;
		const validAudio = !!lineup.audiostreamHlsUrl &&
			(this.form && this.form.form && this.form.form.controls && this.form.form.controls.audiostreamHlsUrl
				&& !this.form.form.controls.audiostreamHlsUrl.hasError('invalid-format'));
		const haveFallbacks = !!(lineup.inputLossImage || lineup.inputLossColor);
		const hlsPushMsg =
			lineup.hlsMethod === 'push'
				&& (!lineup.hlsEndpoint || (lineup.hlsEndpointAuth && (!lineup.hlsEndpointUser || !lineup.hlsEndpointPassword)))
				? this.translate.instant('HLS_CONFIG_INCOMPLETE')
				: '';
		const hlsMsg = lineup.hlsMethod === 'pull' && !lineup.hlsPullUrl ? this.translate.instant('HLS_PULL_STILL_GENERATING') : hlsPushMsg;
		const validHLS = !hlsMsg;

		const lineupChecklistItem = this.checklistItems.find(item => item.key === 'schedule');
		lineupChecklistItem.checked = !this.liveCountExceeded && !this.lineupService.haveCycleGaps;
		lineupChecklistItem.error = this.liveCountExceeded;
		lineupChecklistItem.notification =
			this.liveCountExceeded
				? this.translate.instant('LINEUP_MAX_LIVE_EXCEEDED')
				: (this.lineupService.haveCycleGaps ? this.translate.instant('LINEUP_SCHEDULE_GAPS') : '');

		const fallbackChecklistItem = this.checklistItems.find(item => item.key === 'fallbacks');
		fallbackChecklistItem.checked = haveFallbacks;

		const audiostreamChecklistItem = this.checklistItems.find(item => item.key === 'audiostream');
		audiostreamChecklistItem.checked = validAudio;
		audiostreamChecklistItem.notification = validAudio ? '' : this.translate.instant('LINEUP_INVALID_HLS_AUDIO');

		const hlsChecklistItem = this.checklistItems.find(item => item.key === 'hls');
		hlsChecklistItem.checked = validHLS;
		hlsChecklistItem.warning = !validHLS;
		hlsChecklistItem.notification = hlsMsg;

		this.tabInfoIcons = {};
		this.checklistItems.forEach((item) => {
			if (item.key === 'schedule' && (item.warning || item.error || !item.checked)) {
				this.tabInfoIcons.cycles = item;
			} else if (item.key !== 'schedule' && (item.warning || item.error || !item.checked)) {
				this.tabInfoIcons.configuration = item;
			}
		});
	}

	// When the schedule includes a slideshow-playlist, a HLS audiostream is required
	setContainsSlideshowPlaylist(): void {
		const playlistsIds: string[] = [];
		this.entity.schedule.blocks.forEach(block => {
			block.cycles.forEach(cycle => {
				if (cycle.items) {
					cycle.items.forEach(item => {
						if (item.entityType === 'MediaClipList') {
							playlistsIds.push(item.entityId);
						}
					});
				}
			});
		});

		if (JSON.stringify(this.playlistsInLineupSchedule) === JSON.stringify(playlistsIds)) {
			return;
		}

		this.playlistsInLineupSchedule = playlistsIds;

		if (playlistsIds.length === 0) {
			this.containsSlideshowPlaylist = false;
			this.setCheckAudiostream(false);
			return;
		}

		const queryFilterSet: FilterSet = [{
			filters: [{
				type: 'playlist',
				field: 'id',
				operator: 'equals',
				value: playlistsIds
			}]
		}];

		this.sapiService.query('playlist', [], 0, 999, 'id', 'desc', queryFilterSet).subscribe(queryResponse => {
			const queryItems = queryResponse.items as Playlist[];
			const containsSlideShow = queryItems.some(item => item.isSlideshow);
			this.containsSlideshowPlaylist = containsSlideShow;
			this.setCheckAudiostream(containsSlideShow);
		});
	}

	setCheckAudiostream(required: boolean): void {
		const index = this.checklistItems.findIndex(item => item.key === 'audiostream');
		this.checklistItems[index].hidden = !required;
	}

	ngOnDestroy() {
		this.lineupService.exit();
		this.subscriptions.unsubscribe();
		super.ngOnDestroy();
	}

	load(id: string, duplicating: boolean = false, version?: SapiEntityVersion): Observable<SapiEntity> {
		const superLoad = super.load(id, duplicating, version);
		superLoad.subscribe(((lineup: Lineup) => {
			this.lineupService.initialize(lineup);
			this.loadMediaClipData();
			this.sapiService.getUrl('/lineup/' + id + '/retrieveUsageQuantity').subscribe((response: any) => {
				if (response) {
					this.usageQuantity = response;
					Object.keys(this.usageQuantity).forEach((key: string) => {
						const containsHD = new RegExp('HD').test(key);
						if (containsHD) {
							this.hoursOnline += (parseFloat(this.usageQuantity[key].amount) * 3600) ;
						}
					});
				}
			});
		}));
		return superLoad;
	}

	publish(force: boolean = false, extraHeaders?: any) {
		const dialog: DialogDefinition = {
			icon: 'warning',
			title: this.translate.instant('LINEUP_RESTART_NEEDED'),
			contents: this.entity.restartNeeded
				? this.translate.instant('LINEUP_RESTART_LIVE_CHANGE_NOW')
				: this.translate.instant('LINEUP_RESTART_CONFIG_CHANGE_NOW'),
			onConfirm: () => {
				super.publish(force, extraHeaders);
			}
		};

		if ((this.entity.configChangeRestartNeeded || this.entity.restartNeeded) && this.entity.channelState && this.entity.channelState !== 'IDLE') {

			this.overlayService.createDialog(dialog);
		} else {
			super.publish(force, extraHeaders);
		}
	}

	loadMediaClipData(): void {
		if (this.entity?.hlsMediaClipId) {
			this.sapiService.get('commonclip', this.entity.hlsMediaClipId).subscribe((mediaclip: Mediaclip) => {
				this.mediaclip = mediaclip;
			});
		} else {
			this.mediaclip = null;
		}
	}
}
